/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { queueFetch } from 'app/paypal/components/PayPalView/state/actions';
import React, { useEffect, useState } from 'react';
import { hooks, unstable, utils } from '@broadleaf/admin-components';
import { connect as connectFormik } from 'formik';
import { get, isEmpty, omit, pick } from 'lodash';
import PropTypes from 'prop-types';
import * as qs from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import { HeaderBar } from '@broadleaf/admin-components/dist/common/components/HeaderBar';
import {
  useContextParams,
  usePayPalApi
} from 'app/paypal/components/PayPalView/hooks';
import { ComponentPropShape } from 'app/common/constants/CommonProps';
import {
  defaultReducer,
  defaultState
} from 'app/paypal/components/PayPalView/state/reducers/paypal';

import { WarningTip } from './components';
import messages from './HeaderActionComponents.messages';

const { useActions, useCancelTokenFactory, useEnhancedReducer } = hooks;
const {
  ActionBarDropdown,
  Spinner,
  SubmissionError,
  thunkMiddleware,
  useFormatMessage,
  RequestErrorUtils
} = unstable;
const { log } = utils;
const { getFormikErrorMessages, getResponseGlobalErrors } = RequestErrorUtils;

const logger = log.getLogger(
  'paypal.components.PayPalView.components.HeaderActionComponents'
);

/**
 * Action components for managing PayPal accounts. This will render the
 * actions to provision a new account or resume configuring an account that has
 * not been completed yet.
 *
 * @author Nathan Moore (nathandmoore)
 */
const HeaderActionComponents = ({
  components,
  dismissed,
  formDispatch,
  formState,
  formik,
  metadata
}) => {
  const accountId = get(formState, 'data.value');
  const contextParams = useContextParams(metadata, accountId);
  const formatMessage = useFormatMessage();
  const location = useLocation();
  const history = useHistory();
  const [state, dispatch] = useEnhancedReducer(defaultReducer, defaultState, [
    thunkMiddleware
  ]);
  const [accountFinished, setAccountFinished] = useState(true);
  const [accountEnabled, setAccountEnabled] = useState(true);
  const submissionErrors = getFormikErrorMessages(formik);
  const payPalApi = usePayPalApi(contextParams, dispatch, metadata);
  const [getCancelToken] = useCancelTokenFactory();
  const secondaryActions = useActions(metadata, 'SECONDARY');

  useEffect(() => {
    const search = qs.parse(location.search);

    if (search.payPalRefresh) {
      const data = pick(search, PayPalAccountParams);
      const {
        accountStatus,
        isEmailConfirmed,
        merchantIdInPayPal: merchantId,
        permissionsGranted
      } = data;
      setAccountFinished(
        accountStatus === 'BUSINESS_ACCOUNT' && !!isEmailConfirmed
      );
      setAccountEnabled(!!permissionsGranted);
      (async () => {
        try {
          await payPalApi.accounts.create({
            cancelToken: getCancelToken(),
            data,
            contextParams: {
              id: merchantId,
              accountId: merchantId,
              parent: {
                value: merchantId
              },
              property: {
                value: merchantId
              }
            },
            rejectOnError: true
          });

          setTimeout(() => {
            history.replace({
              search: qs.stringify(
                omit(search, ['payPalRefresh', ...PayPalAccountParams])
              )
            });
            formDispatch(queueFetch());
          }, 300);

          formik.setFieldValue('value', merchantId);
        } catch (err) {
          logger.error(
            'Encountered an error trying to create a PayPal account connection',
            err
          );
        }
      })();
    }
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    if (formState.isFetching || formState.isFetchError) {
      return;
    }

    const { finished, enabled } = formState.data || {};

    if (finished === undefined) {
      return;
    }

    setAccountFinished(formik.values.finished || finished);
    setAccountEnabled(formik.values.enabled || enabled);
  }, [formik, formState]);

  return (
    <>
      <HeaderBar.Element className="tw-flex tw-flex-row tw-items-center tw-justify-between">
        {!isEmpty(submissionErrors) && (
          <SubmissionError errors={submissionErrors} />
        )}
        {state.isFetchError && (
          <SubmissionError errors={getErrors(state.fetchError)} />
        )}
        {isEmpty(accountId) ? null : !accountFinished ? (
          <WarningTip
            message={formatMessage(messages.resumeConfigurationWarning)}
          />
        ) : (
          !accountEnabled && (
            <WarningTip
              baseColor="red"
              message={formatMessage(messages.accountDisabled)}
            />
          )
        )}
        {formik.isSubmitting && <Spinner color="info" />}
        {isEmpty(accountId) ? (
          <components.CreateAccount
            dispatch={dispatch}
            dismissed={dismissed}
            disabled={formState.isFetching || formState.isFetchQueued}
            metadata={metadata}
            payPalApi={payPalApi}
            state={state}
            setAccountFinished={setAccountFinished}
          />
        ) : accountFinished ? (
          <components.UpdateAccount
            accountId={accountId}
            disabled={formState.isFetching || formState.isFetchQueued}
            dispatch={dispatch}
            payPalApi={payPalApi}
            state={state}
          />
        ) : (
          <components.CreateAccount
            disabled={formState.isFetching || formState.isFetchQueued}
            dismissed
            dispatch={dispatch}
            finish
            metadata={metadata}
            payPalApi={payPalApi}
            setAccountFinished={setAccountFinished}
            state={state}
          />
        )}
      </HeaderBar.Element>
      {!isEmpty(secondaryActions) && !isEmpty(accountId) && (
        <HeaderBar.Element className="tw-ml-3">
          <ActionBarDropdown
            actions={secondaryActions}
            actionProps={{
              accountId,
              accountFinished,
              dispatch,
              setAccountFinished,
              payPalApi,
              state
            }}
          />
        </HeaderBar.Element>
      )}
    </>
  );
};

function getErrors(fetchError) {
  const globalErrors = getResponseGlobalErrors(fetchError);

  if (!isEmpty(globalErrors)) {
    return globalErrors;
  }

  if (!isEmpty(fetchError.stack)) {
    return fetchError.stack;
  }

  return fetchError.type;
}

HeaderActionComponents.propTypes = {
  components: PropTypes.shape({
    CreateAccount: PropTypes.func.isRequired,
    UpdateAccount: PropTypes.func.isRequired
  }).isRequired,
  /** Whether submitting the account ID System Property form is disabled */
  disableSubmit: PropTypes.bool,
  dismissed: PropTypes.bool,
  /** The {@link FormikBag} object. */
  formik: PropTypes.object.isRequired,
  formState: PropTypes.shape({}),
  metadata: ComponentPropShape
};

const PayPalAccountParams = Object.freeze([
  'merchantId',
  'merchantIdInPayPal',
  'permissionsGranted',
  'accountStatus',
  'consentStatus',
  'productIntentId',
  'isEmailConfirmed',
  'returnMessage',
  'riskStatus',
  'productIntentID'
]);

export default connectFormik(HeaderActionComponents);
export { HeaderActionComponents };
